import axiosInstance from "./axiosConfig";
import { handleAPIError } from "./utils/errors";
import {
  CreateForeignNationalMatterRequest,
  ListMattersParams,
  MatterDTO,
  MatterStatsResponse,
  ListMattersResponse,
  UpdateMatterRequest
} from "./types/matter";
import { VISAVALUE } from "../types/visa.type";
import type { UserProfile } from "./types";

const BASE_URL = "/matters";
const FOREIGN_NATIONALS_URL = "/foreign_nationals";

export type CreateMatterRequest = {
  individual: {
    first_name: string;
    last_name: string;
    email: string;
    visa?: VISAVALUE;
    title?: string;
    pronouns?: string;
  };
  case?: Record<string, unknown>;
};

export const MattersAPI = {
  async list(params: ListMattersParams = {}): Promise<ListMattersResponse> {
    try {
      const response = await axiosInstance.get<ListMattersResponse>(BASE_URL, {
        params
      });
      const raw = response.data;
      const matters = Array.isArray(raw?.matters)
        ? raw.matters.map(normalizeMatterDTO)
        : [];
      return {
        total_count: Number.isFinite(raw?.total_count) ? raw.total_count : 0,
        matters
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async stats(
    params: Pick<ListMattersParams, "lawyer_id" | "law_firm_id"> = {}
  ): Promise<MatterStatsResponse> {
    try {
      const response = await axiosInstance.get<MatterStatsResponse>(
        `${BASE_URL}/stats`,
        { params }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async get(individualId: string): Promise<MatterDTO> {
    try {
      const response = await axiosInstance.get<MatterDTO>(
        `${BASE_URL}/${individualId}`
      );
      return normalizeMatterDTO(response.data);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async create(payload: CreateMatterRequest): Promise<MatterDTO> {
    try {
      const response = await axiosInstance.post<MatterDTO>(BASE_URL, payload);
      return normalizeMatterDTO(response.data);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async update(
    individualId: string,
    updates: UpdateMatterRequest
  ): Promise<MatterDTO> {
    try {
      const response = await axiosInstance.put<MatterDTO>(
        `${BASE_URL}/${individualId}`,
        updates
      );
      return normalizeMatterDTO(response.data);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async delete(individualId: string): Promise<void> {
    try {
      await axiosInstance.delete(`${BASE_URL}/${individualId}`);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listForForeignNational(
    foreignNationalId: string
  ): Promise<MatterDTO[]> {
    try {
      const response = await axiosInstance.get<MatterDTO[]>(
        `${FOREIGN_NATIONALS_URL}/${foreignNationalId}/matters`
      );
      return Array.isArray(response.data)
        ? response.data.map(normalizeMatterDTO)
        : [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createForForeignNational(
    foreignNationalId: string,
    payload: CreateForeignNationalMatterRequest
  ): Promise<MatterDTO> {
    try {
      const response = await axiosInstance.post<MatterDTO>(
        `${FOREIGN_NATIONALS_URL}/${foreignNationalId}/matters`,
        payload
      );
      return normalizeMatterDTO(response.data);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};

function normalizeUserProfile(value: unknown): UserProfile | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  const uid = typeof v.uid === "string" ? v.uid : "";
  if (!uid.trim()) return null;

  return {
    uid,
    first_name: typeof v.first_name === "string" ? v.first_name : null,
    last_name: typeof v.last_name === "string" ? v.last_name : null,
    email: typeof v.email === "string" ? v.email : null,
    avatar_data_url:
      typeof v.avatar_data_url === "string" ? v.avatar_data_url : null
  };
}

function normalizeUserId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const id = value.trim();
  return id || null;
}

function normalizeUserIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeUserId)
    .filter((id): id is string => typeof id === "string" && Boolean(id));
}

function normalizeMatterDTO(matter: MatterDTO): MatterDTO {
  const m = (matter ?? {}) as unknown as Record<string, unknown>;

  const created_by_user_id =
    normalizeUserId(m.created_by_user_id) ??
    normalizeUserId(m.created_by_user_uid) ??
    normalizeUserId(m.created_by_uid) ??
    null;

  const assignedProfilesRaw =
    m.assigned_to_user_profiles ??
    // Alternate backend spellings we’ve seen in other DTOs.
    m.assigned_to_user_profile ??
    m.lawyer_profile ??
    null;

  const assigned_to_user_profiles = Array.isArray(assignedProfilesRaw)
    ? assignedProfilesRaw.map(normalizeUserProfile)
    : assignedProfilesRaw
    ? [normalizeUserProfile(assignedProfilesRaw)]
    : [];

  const individual = m.individual as Record<string, unknown> | undefined;
  const assignedIdsTopLevel = normalizeUserIds(m.assigned_to_user_ids);
  const assignedIdsFromIndividual = normalizeUserIds(
    individual?.assigned_to_user_ids
  );
  const assignedIdsFromField =
    assignedIdsTopLevel.length > 0
      ? assignedIdsTopLevel
      : assignedIdsFromIndividual;
  const assigned_to_user_ids =
    assignedIdsFromField.length > 0
      ? assignedIdsFromField
      : assigned_to_user_profiles
          .map((p) => p?.uid ?? "")
          .map((id) => id.trim())
          .filter(Boolean);

  return {
    ...(matter as MatterDTO),
    created_by_user_id,
    assigned_to_user_ids,
    assigned_to_user_profiles
  };
}
